import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

// PATCH /leads/{id} (update_lead_basic(), Commit 13) — limpia needs_website
// automáticamente al fijar website_url, no se duplica esa lógica aquí.
export function EditLeadModal({ open, onOpenChange, lead, onSaved }) {
  const { api } = useAuth();
  const { t } = useTranslation();

  const [form, setForm] = useState({ phone: '', website_url: '', email: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Se re-sincroniza con los datos reales del lead cada vez que el modal
  // se abre — evita editar sobre valores obsoletos si el lead cambió
  // desde la última vez (p.ej. tras un re-análisis).
  useEffect(() => {
    if (open && lead) {
      setForm({
        phone: lead.phone || '',
        website_url: lead.website_url || '',
        email: lead.email || '',
        notes: lead.notes || '',
      });
    }
  }, [open, lead]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [key, value] of Object.entries(form)) {
        payload[key] = value.trim() ? value.trim() : undefined;
      }
      await api.patch(`/leads/${lead.id}`, payload);
      toast.success(t('aiLeadScanner.editLeadModal.success'));
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      console.error('Failed to update lead:', error);
      toast.error(t('aiLeadScanner.editLeadModal.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('aiLeadScanner.editLeadModal.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>{t('aiLeadScanner.addLeadModal.phone')}</Label>
            <Input value={form.phone} onChange={handleChange('phone')} className="mt-1" />
          </div>

          <div>
            <Label>{t('aiLeadScanner.leadDetail.website')}</Label>
            <Input value={form.website_url} onChange={handleChange('website_url')} className="mt-1" placeholder="https://" />
          </div>

          <div>
            <Label>{t('aiLeadScanner.addLeadModal.email')}</Label>
            <Input value={form.email} onChange={handleChange('email')} className="mt-1" type="email" />
          </div>

          <div>
            <Label>{t('aiLeadScanner.leadDetail.notes')}</Label>
            <Textarea value={form.notes} onChange={handleChange('notes')} className="mt-1" rows={3} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              {t('aiLeadScanner.leadDetail.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="edit-lead-save">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {t('aiLeadScanner.leadDetail.save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
